import { ErrorModal, useContextMenuMESF } from "@dexteel/mesf-core";
import Grid2 from "@mui/material/Grid2";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import {
	DefaultMenuItem,
	GetContextMenuItemsParams,
	IRowNode,
	MenuItemDef,
	RowDoubleClickedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useEffect, useMemo, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { FooterButton } from "@/controls/buttons/FooterButton";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import { useAreaIds } from "@/pages/workOrders/hooks/useAreaIds";
import {
	BreakRule,
	BreakRulePreset,
	ShiftHours,
} from "@/shared/models/BreakRule";
import { useBreakRules } from "../hooks/useBreakRules";
import {
	autoGroupColumnDef,
	type BreakRuleModal,
	type GroupTarget,
	type MenuTarget,
	useTableData,
} from "../hooks/useTableData";
import {
	type BreakGridRow,
	buildGridRows,
	getGridRowId,
	groupKind,
} from "../utils/gridRows";
import {
	BREAK_SCHEDULE_FILTERS_INITIAL,
	BreakScheduleFilters,
	type BreakScheduleFiltersState,
} from "./BreakScheduleFilters";
import { SchemeFooterRow } from "./SchemeFooterRow";
import { UpsertDeleteModal } from "./UpsertDeleteModal";

const toGroupTarget = (
	node: IRowNode<BreakGridRow>,
): GroupTarget | undefined => {
	const first = node.allLeafChildren?.[0]?.data;
	if (!first) return undefined;
	const kind = groupKind(node);
	return {
		kind: "group",
		level: node.level,
		AssetId: first.AssetId,
		AssetName: first.AssetName,
		ShiftHours:
			kind === "shift"
				? (Number(node.key?.replace("h", "")) as ShiftHours)
				: undefined,
		DefaultShiftHours: first.DefaultShiftHours,
	};
};

export const TableBreakRules = () => {
	const [showModal, setShowModal] = useState<BreakRuleModal>("");
	const [selectedRowData, setSelectedRowData] = useState<BreakRule | null>(
		null,
	);
	const [preset, setPreset] = useState<BreakRulePreset | null>(null);
	const [error, setError] = useState("");
	const [filters, setFilters] = useState<BreakScheduleFiltersState>(
		BREAK_SCHEDULE_FILTERS_INITIAL,
	);

	const { areaIds } = useAreaIds();
	const { data, isLoading } = useBreakRules((e) => setError(e.message));
	const rows = useMemo(
		() => buildGridRows(data?.lines ?? [], data?.rules ?? [], filters, areaIds),
		[data, filters.assetId, filters.shiftHours, areaIds],
	);

	const openCreate = (nextPreset: BreakRulePreset | null) => {
		setPreset(nextPreset);
		setShowModal("create");
	};

	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const { columnDefs, getMenuOptions } = useTableData({
		setSelectedRowData,
		setShowModal,
		setPreset,
		showContextMenu,
	});

	const { defaultColDef } = useGridDefinitions({});

	const onRowDoubleClicked = (event: RowDoubleClickedEvent<BreakGridRow>) => {
		if (event.data?.kind !== "rule") return;
		setSelectedRowData(event.data);
		setShowModal("update");
	};

	const getContextMenuItems = useCallback(
		(
			params: GetContextMenuItemsParams<BreakGridRow>,
		): (DefaultMenuItem | MenuItemDef)[] => {
			const node = params.node;
			const target: MenuTarget = node?.group ? toGroupTarget(node) : node?.data;
			showContextMenu(event as any, target, "breakRulesContext");
			if (target?.kind === "rule") {
				params.api.deselectAll();
				node?.setSelected(true);
			}
			return [];
		},
		[rows],
	);

	useEffect(() => {
		registerConfig({
			id: "breakRulesContext",
			getOptions: getMenuOptions,
		});
	}, []);

	return (
		<>
			<Grid2 container justifyContent="center" p={1} spacing={1}>
				<Grid2
					container
					size={{ md: 12, xs: 12 }}
					spacing={1}
					direction={"column"}
				>
					<Grid2>
						<Typography variant="h5" fontWeight={600}>
							Break Schedules
						</Typography>
						<Typography variant="body2" color="text.secondary">
							Default breaks per line and shift length, anchored to the start of
							each shift. A shift length with no breaks of its own uses the line
							default's. The scheduler creates them on the calendar
							automatically; move or delete a specific day's break from the
							calendar itself.
						</Typography>
					</Grid2>
				</Grid2>
				<Grid2 container justifyContent="center" alignItems="center">
					<Grid2 size={{ md: 12, xs: 12 }} style={{ marginBottom: 10 }}>
						<BreakScheduleFilters
							filters={filters}
							lines={data?.lines ?? []}
							onChange={setFilters}
						/>
					</Grid2>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Paper elevation={2} style={{ height: "68vh", width: "100%" }}>
							<AgGridReact<BreakGridRow>
								loading={isLoading}
								rowData={rows}
								columnDefs={columnDefs}
								defaultColDef={defaultColDef}
								autoGroupColumnDef={autoGroupColumnDef}
								treeData
								getDataPath={(row) => row.path}
								includeHiddenColumnsInQuickFilter
								groupDefaultExpanded={0}
								isGroupOpenByDefault={(params) =>
									groupKind(params.rowNode) !== "line"
								}
								isFullWidthRow={(params) => params.rowNode.data?.kind === "add"}
								fullWidthCellRenderer={SchemeFooterRow}
								fullWidthCellRendererParams={{ onNew: openCreate }}
								rowHeight={34}
								headerHeight={30}
								animateRows
								getContextMenuItems={getContextMenuItems}
								onRowDoubleClicked={onRowDoubleClicked}
								loadingOverlayComponent={CenteredLazyLoading}
								getRowId={(params) => getGridRowId(params.data)}
								quickFilterText={filters.quickFilterText}
								gridOptions={{
									rowSelection: {
										mode: "singleRow",
										checkboxes: false,
										enableClickSelection: true,
									},
								}}
							/>
						</Paper>
					</Grid2>
					<FooterButton
						label="BREAK"
						showCreateModal={showModal === "create"}
						setShowCreateModal={(show) =>
							show ? openCreate(null) : setShowModal("")
						}
					/>
				</Grid2>
			</Grid2>
			<ErrorModal error={error} onHide={() => setError("")} />
			<UpsertDeleteModal
				breakRule={selectedRowData}
				preset={preset}
				modal={showModal}
				onHide={() => setShowModal("")}
			/>
		</>
	);
};
